import {
  CLOSEOUT_COLUMNS,
  STAGE_LABEL,
  cents,
  closeoutColumn,
  daysSince,
  formatMoney,
  jobAddress,
  jobMatches,
  type Cents,
  type CloseoutColumn,
  type Job,
  type Profile,
} from "@roof-hawk/core";
import { useMemo, useState } from "react";

import { ErrorNote, ListHead } from "../../components/ui";
import { t, tn } from "../../i18n";
import {
  closeJob,
  fetchClosedTicks,
  reopenJob,
  type CloseoutRow,
} from "../../lib/closeout";
import { fmtDate } from "../../lib/format";
import { navigate, useAsync } from "../../lib/hooks";
import { fetchProductionBoard } from "../../lib/queries";
import { useSession } from "../../session";
import type { AppData } from "../shell/Shell";
import { boardBalanceFor, type ProductionBoardData } from "./boardBalance";

/**
 * Kelsey's board — every sold job, and the ones waiting for her to close.
 *
 * Jonathon, 2026-10-06: "please build a pipeline for her of signed jobs and
 * forward so she can easily see where each lead is in the process." Every
 * rep's jobs, not just one person's, in four columns: Signed, In Production,
 * Work Complete and Ready to Close. A job reaches the last one when its rep
 * presses Send to Kelsey; Close takes it off, onto the Closed list.
 *
 * The columns are `closeoutColumn` in packages/core. Anyone on staff can Close
 * and Reopen (Jonathon, 2026-10-12: "anyone can close them"); the database
 * refuses only a login with no staff role (ADR 0273).
 */
export function CloseoutBoard({
  owner,
  data,
  search,
}: {
  owner: Profile;
  data: AppData;
  search: string;
}) {
  const [showClosed, setShowClosed] = useState(false);
  const closeouts = data.closeouts;
  const count = data.jobs.filter((job) =>
    showClosed
      ? job.stage === "closed"
      : closeoutColumn(job, Boolean(closeouts?.has(job.id))) !== null,
  ).length;

  return (
    <>
      <ListHead
        title={t(showClosed ? "{name} — Closed" : "{name} — Close Out", {
          name: String(owner.full_name),
        })}
        count={count}
      >
        <button
          className="btn-ghost closeout-toggle"
          aria-pressed={showClosed}
          onClick={() => setShowClosed((v) => !v)}
        >
          {showClosed ? t("Back to the board") : t("Closed jobs")}
        </button>
      </ListHead>
      {showClosed ? (
        <ClosedList data={data} search={search} />
      ) : (
        <Board data={data} search={search} />
      )}
    </>
  );
}

function Board({ data, search }: { data: AppData; search: string }) {
  const { people } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const closeouts = useMemo(
    () => data.closeouts ?? new Map<string, CloseoutRow>(),
    [data.closeouts],
  );

  const placed = useMemo(
    () =>
      data.jobs
        .filter((job) => jobMatches(job, search))
        .map((job) => ({
          job,
          column: closeoutColumn(job, closeouts.has(job.id)),
        }))
        .filter(
          (p): p is { job: Job; column: CloseoutColumn } => p.column !== null,
        ),
    [data.jobs, search, closeouts],
  );

  const jobIds = useMemo(() => placed.map((p) => p.job.id), [placed]);
  const board = useAsync(
    () => fetchProductionBoard(jobIds),
    [jobIds.join(",")],
  );

  /** When Final Check was ticked, for how long a job has sat at Work Complete. */
  const finalCheckAt = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const item of board.data?.items ?? [])
      if (item.label === "Final Check" && item.status === "done")
        map.set(item.job_id, item.completed_at ?? null);
    return map;
  }, [board.data]);

  const nameOf = (id: string | null | undefined) =>
    people.find((p) => p.id === id)?.full_name ?? t("someone");

  async function close(job: Job) {
    setBusy(job.id);
    setError(null);
    try {
      await closeJob(job.id);
      await data.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const now = new Date();

  return (
    <>
      <ErrorNote error={error ?? board.error ?? null} />
      <div className="pipe four">
        {CLOSEOUT_COLUMNS.map((column) => {
          const jobs = placed
            .filter((p) => p.column === column.key)
            .map((p) => p.job);
          return (
            <div
              className={`pcol tone-${column.key === "ready_to_close" ? "green" : "blue"}`}
              key={column.key}
              data-column={column.key}
            >
              <h3>
                <span>{t(column.title)}</span>
                <span className="c">{jobs.length}</span>
              </h3>
              {jobs.length === 0 ? (
                <div className="none">{t("None")}</div>
              ) : (
                jobs.map((job) => (
                  <BoardCard
                    key={job.id}
                    job={job}
                    column={column.key}
                    sent={closeouts.get(job.id)}
                    finalCheckAt={finalCheckAt.get(job.id)}
                    contract={data.values?.get(job.id) ?? cents(0)}
                    board={board.data}
                    sentBy={nameOf(closeouts.get(job.id)?.sent_by)}
                    busy={busy === job.id}
                    onClose={() => void close(job)}
                    now={now}
                  />
                ))
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

/** How long a job has sat in its column: since it got there. */
function arrivedAt(
  column: CloseoutColumn,
  job: Job,
  sent: CloseoutRow | undefined,
  finalCheckAt: string | null | undefined,
): string | null | undefined {
  if (column === "ready_to_close") return sent?.sent_at;
  if (column === "work_complete") return finalCheckAt;
  if (column === "in_production") return job.sends_at;
  return job.signed_contract_at;
}

/** "Job 1077 · Samuel Sandoval · 3 days here" */
function cardMeta(job: Job, days: number | null): string {
  const here =
    days == null ? "" : ` · ${tn(days, "1 day here", "{n} days here")}`;
  return t("Job {number} · {rep}{here}", {
    number: job.job_number ?? "—",
    rep: job.rep?.full_name ?? t("no rep"),
    here,
  });
}

/** Who sent it and when, and how much was flagged at the time. */
function SentLines({ sent, sentBy }: { sent: CloseoutRow; sentBy: string }) {
  const flagged = sent.warnings?.length ?? 0;
  return (
    <>
      <div className="pmeta">
        {t("Sent by {who} on {date}", {
          who: sentBy,
          date: fmtDate(sent.sent_at),
        })}
      </div>
      {flagged > 0 && (
        <div className="pflag">
          {tn(
            flagged,
            "1 thing flagged when sent",
            "{n} things flagged when sent",
          )}
        </div>
      )}
    </>
  );
}

/** Close, for anyone on staff. */
function CloseControl({
  busy,
  onClose,
}: {
  busy: boolean;
  onClose: () => void;
}) {
  return (
    <button className="btn-primary pclose" disabled={busy} onClick={onClose}>
      {busy ? t("Closing…") : t("Close")}
    </button>
  );
}

/** One job on the board; on Ready to Close, with Close under it. */
function BoardCard({
  job,
  column,
  sent,
  finalCheckAt,
  contract,
  board,
  sentBy,
  busy,
  onClose,
  now,
}: {
  job: Job;
  column: CloseoutColumn;
  sent: CloseoutRow | undefined;
  finalCheckAt: string | null | undefined;
  contract: Cents;
  board: ProductionBoardData | null;
  sentBy: string;
  busy: boolean;
  onClose: () => void;
  now: Date;
}) {
  const days = daysSince(arrivedAt(column, job, sent, finalCheckAt), now);
  const balance = board ? boardBalanceFor(board, job.id, contract) : null;
  const owes = balance && balance.outstanding > 0;
  return (
    <div className="closeout-card">
      {/* A real link into its own tab, like a rep's card, so working the
          board never loses her place on it. */}
      <a
        className="pcard replacement"
        href={`#job=${job.id}`}
        target="_blank"
        rel="noreferrer"
      >
        <div className="pcard-top">
          <div className="cn">{job.contact?.full_name ?? "—"}</div>
          <div className="pval">{formatMoney(contract)}</div>
        </div>
        <div className="ca">{jobAddress(job)}</div>
        <div className="pmeta">{cardMeta(job, days)}</div>
        {owes && (
          <div className="powed">
            {t("Owes {amount}", { amount: balance.outstandingFormatted })}
          </div>
        )}
        {sent && <SentLines sent={sent} sentBy={sentBy} />}
        <div className="pstage">{t(STAGE_LABEL[job.stage])}</div>
      </a>
      {column === "ready_to_close" && (
        <CloseControl busy={busy} onClose={onClose} />
      )}
    </div>
  );
}

/**
 * Closed jobs, newest first, each with a way back.
 *
 * Reopen unticks Closed and puts the job back in Ready to Close. Built that
 * way so a job closed by mistake is one press from where it was (ADR 0273).
 */
function ClosedList({ data, search }: { data: AppData; search: string }) {
  const { people } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const closeouts = data.closeouts ?? new Map<string, CloseoutRow>();

  const closed = useMemo(
    () =>
      data.jobs.filter(
        (job) => job.stage === "closed" && jobMatches(job, search),
      ),
    [data.jobs, search],
  );
  const ids = useMemo(() => closed.map((j) => j.id), [closed]);
  const ticks = useAsync(() => fetchClosedTicks(ids), [ids.join(",")]);

  const rows = useMemo(
    () =>
      [...closed].sort((a, b) =>
        (ticks.data?.get(b.id)?.at ?? "").localeCompare(
          ticks.data?.get(a.id)?.at ?? "",
        ),
      ),
    [closed, ticks.data],
  );

  const nameOf = (id: string | null | undefined) =>
    id ? (people.find((p) => p.id === id)?.full_name ?? t("someone")) : "—";

  async function reopen(job: Job) {
    setBusy(job.id);
    setError(null);
    try {
      await reopenJob(job.id);
      await Promise.all([data.reload(), ticks.reload()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  if (rows.length === 0)
    return (
      <div className="list">
        <div className="empty">
          <b>{t("No closed jobs")}</b>
        </div>
      </div>
    );

  return (
    <>
      <ErrorNote error={error ?? ticks.error ?? null} />
      <div className="list closed-list">
        {rows.map((job) => {
          const sent = closeouts.get(job.id);
          const tick = ticks.data?.get(job.id);
          return (
            <div
              className="lrow"
              key={job.id}
              onClick={() => navigate(`job=${job.id}`)}
            >
              <span>{job.contact?.full_name ?? "—"}</span>
              <span>{job.job_number ?? "—"}</span>
              <span>{job.rep?.full_name ?? t("no rep")}</span>
              <span>{formatMoney(data.values?.get(job.id) ?? cents(0))}</span>
              <span>
                {sent
                  ? t("Sent by {who}, {date}", {
                      who: nameOf(sent.sent_by),
                      date: fmtDate(sent.sent_at),
                    })
                  : t("Not sent")}
              </span>
              <span>
                {tick
                  ? t("Closed by {who}, {date}", {
                      who: nameOf(tick.by),
                      date: fmtDate(tick.at),
                    })
                  : t("Closed")}
              </span>
              <span>
                <button
                  className="btn-ghost"
                  disabled={busy === job.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    void reopen(job);
                  }}
                >
                  {busy === job.id ? t("Reopening…") : t("Reopen")}
                </button>
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
}
